import type { GameView } from "../../server/protocol";
export type PublicSeat = {
  id: string;
  name: string;
  seat: number;
  alive: boolean;
  connected: boolean | null;
  diceCount: number | null;
};
export type PublicProgress = { submitted: number; eligible: number };
export type PublicTableModel = {
  seats: PublicSeat[];
  actorId: string | null;
  nextIds: string[];
  progress: PublicProgress | null;
};
type Row = Record<string, unknown>;
function rows(value: unknown): Row[] {
  return Array.isArray(value)
    ? value.filter((v): v is Row => Boolean(v) && typeof v === "object")
    : [];
}
export function publicTableModel(
  game: Pick<GameView, "id" | "phase" | "public">,
  connections: ReadonlyArray<{ id: string; connected: boolean }>,
): PublicTableModel {
  const { id, phase, public: p } = game;
  const counts = new Map(rows(p.diceCounts).map((v) => [v.playerId, v.count]));
  const seats: PublicSeat[] = rows(p.players)
    .filter((v) => typeof v.id === "string" && typeof v.name === "string")
    .map((v, i) => ({
      id: v.id as string,
      name: v.name as string,
      seat: i + 1,
      alive: v.alive !== false,
      connected: connections.find((c) => c.id === v.id)?.connected ?? null,
      diceCount:
        id === "liar-dice" && Number.isInteger(counts.get(v.id))
          ? Math.max(0, Number(counts.get(v.id)))
          : null,
    }));
  const living = seats.filter((s) => s.alive),
    ids = seats.map((s) => s.id);
  let actor: unknown = null,
    eligible: string[] | null = null;
  if (id === "impostor" && phase === "clue") actor = p.turnPlayerId;
  if (id === "alibi")
    actor =
      phase === "question"
        ? p.turnPlayerId
        : phase === "answer"
          ? p.answerPlayerId
          : phase === "guess"
            ? p.accusedId
            : null;
  if (id === "impostor" && phase === "guess") actor = p.accusedId;
  if (id === "liar-dice" && phase === "bidding") actor = p.turnPlayerId;
  if (id === "spectrum" && phase === "clue") actor = p.guideId;
  if (id === "secret-hitler") {
    if (
      [
        "nomination",
        "president-discard",
        "veto-response",
        "executive",
      ].includes(phase)
    )
      actor = p.presidentId;
    if (phase === "chancellor-enact") actor = p.chancellorId;
    if (phase === "election") eligible = living.map((s) => s.id);
  }
  if (["impostor", "alibi"].includes(id) && ["vote", "runoff"].includes(phase))
    eligible = ids;
  if (id === "mafia" && phase === "vote") eligible = living.map((s) => s.id);
  if (id === "spectrum" && phase === "guess")
    eligible = living.filter((s) => s.id !== p.guideId).map((s) => s.id);
  const actorId =
    typeof actor === "string" && ids.includes(actor) ? actor : null;
  let nextIds: string[] = [];
  if (id === "impostor" && phase === "clue" && actorId && ids.length) {
    const given = rows(p.clues).filter((c) => c.pass === p.cluePass).length;
    const start = ids.indexOf(actorId);
    nextIds = Array.from(
      { length: Math.max(0, ids.length - given - 1) },
      (_, i) => ids[(start + i + 1) % ids.length],
    );
  }
  const submitted = new Set(
    Array.isArray(p.submittedPlayerIds) ? p.submittedPlayerIds : [],
  );
  const progress = eligible
    ? {
        submitted: eligible.filter((v) => submitted.has(v)).length,
        eligible: eligible.length,
      }
    : null;
  return { seats, actorId, nextIds, progress };
}
export function publicSeatName(
  seats: readonly PublicSeat[],
  id: string | null | undefined,
): string {
  const seat = seats.find((s) => s.id === id);
  if (!seat) return "—";
  return seats.filter((s) => s.name === seat.name).length > 1
    ? `${seat.name} #${seat.seat}`
    : seat.name;
}
/** Seats whose public connection state is offline (room `players[].connected`). */
export function offlineSeatIds(
  connections: ReadonlyArray<{ id: string; connected: boolean }> | undefined,
): string[] {
  return (connections ?? []).filter((c) => c.connected === false).map((c) => c.id);
}
